/**
 * Adhesive Block Renderer
 * Renders the anchor product "Complete Your Install" flow.
 */
class AdhesiveBlockRenderer {
  /**
   * Render install recommendations for a product page block.
   * @param {Object} config
   * @param {string} config.blockId
   * @param {number} config.currentProductId
   * @param {Object} config.settings
   * @param {string} config.containerId
   */
  static renderRecommendations(config) {
    const { currentProductId, containerId } = config;
    const isKit = config.settings?.isKit === true;
    const recommendations = isKit
      ? []
      : AdhesiveCalculatorCore.getAdhesiveRecommendations(currentProductId, 1);
    const container = document.getElementById(containerId);

    if (!container) return;

    if (recommendations.length === 0 && !isKit) {
      container.style.display = 'none';
      return;
    }

    const ownedQuantity = container.querySelector?.('[data-aac-owned-quantity]');
    ownedQuantity?.remove();

    this.renderRecommendationUI(recommendations, config);
    if (ownedQuantity) {
      this.placeOwnedQuantitySelector(container, ownedQuantity);
    } else {
      this.moveQuantitySelectorIntoInstallFlow(container, config.blockId);
    }
    this.setupEventHandlers(config, currentProductId, recommendations);
  }

  static renderRecommendationUI(recommendations, config) {
    const { blockId, settings, containerId } = config;
    const container = document.getElementById(containerId);

    if (!container) return;

    const groupedAdhesives = this.groupAdhesivesByProduct(recommendations);
    const addOns = Array.isArray(settings.addOns) ? settings.addOns : [];
    const essentialAddOns = addOns.filter(addOn => addOn.group === 'essential');
    const recommendedAddOns = addOns.filter(addOn => addOn.group === 'recommended');
    const optionalAddOns = addOns.filter(addOn => addOn.group === 'optional');
    const labels = settings.labels || {};
    const lowestVariantIds = AdhesiveCalculatorCore.getLowestTotalVariantIdsByProduct(recommendations);

    container.dataset.mainProductPrice = String(settings.mainProductPrice || 0);
    container.dataset.variantPrices = JSON.stringify(settings.variantPrices || {});

    container.innerHTML = `
      <div class="aac-install-header-${blockId}">
        <span class="aac-install-check-icon-${blockId}" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="32" height="32" fill="none">
            <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="2"></circle>
            <path d="m8 12 2.6 2.6L16 9.2" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"></path>
          </svg>
        </span>
        <div>
          <h3 class="aac-install-title-${blockId}">${this.escapeHtml(settings.title || 'Complete Your Install')}</h3>
          ${settings.subtitle ? `<p class="aac-install-subtitle-${blockId}">${this.escapeHtml(settings.subtitle)}</p>` : ''}
        </div>
      </div>

      <div class="aac-install-quantity-${blockId}" data-anchor-quantity-slot>
        <span class="aac-install-quantity-label-${blockId}">${this.escapeHtml(settings.isKit ? labels.numberOfKits : labels.numberOfSixPacks)}</span>
      </div>

      ${settings.isKit ? `
        <div class="aac-install-section-${blockId}" data-install-section="included-adhesive">
          <div class="aac-install-included-adhesive-${blockId}">
            <strong>${this.escapeHtml(labels.includedWithKit)}: ${this.escapeHtml(settings.includedAdhesiveLabel || settings.includedAdhesiveSystem)}</strong>
            ${labels.includedInKitNote ? `<span>${this.escapeHtml(labels.includedInKitNote)}</span>` : ''}
          </div>
        </div>
      ` : `
        <div class="aac-install-section-${blockId}" data-install-section="adhesive">
          <div class="aac-install-section-heading-${blockId}">
            <span class="aac-install-pill-${blockId} aac-install-pill--required-${blockId}">${this.escapeHtml(labels.requiredForInstallation)}</span>
            <h4 class="aac-install-section-title-${blockId}">${this.escapeHtml(labels.chooseAdhesive)}${this.renderHelpLink(blockId, settings.adhesiveHelpUrl, labels.adhesive)}</h4>
          </div>
          <p class="aac-install-choice-hint-${blockId}">${this.escapeHtml(labels.choiceHint)}</p>
          <fieldset class="aac-install-adhesive-choices-${blockId}">
            <legend class="visually-hidden">${this.escapeHtml(labels.chooseAdhesive)}</legend>
            ${groupedAdhesives.map(group => this.renderAdhesiveGroup(group, blockId, labels, lowestVariantIds)).join('')}
            <div class="aac-install-choice-divider-${blockId}"><span>${this.escapeHtml(labels.or)}</span></div>
            ${this.renderNoAdhesiveOption(blockId, labels)}
          </fieldset>
        </div>
      `}

      ${essentialAddOns.length ? `
        <div class="aac-install-section-${blockId}" data-install-section="essential">
          ${essentialAddOns.map(addOn => this.renderAddOnRow(addOn, blockId)).join('')}
        </div>
      ` : ''}

      ${recommendedAddOns.length ? `
        <div class="aac-install-section-${blockId}" data-install-section="recommended">
          <div class="aac-install-section-heading-${blockId}">
            <span class="aac-install-pill-${blockId} aac-install-pill--filled-${blockId}">Recommended</span>
          </div>
          ${recommendedAddOns.map(addOn => this.renderAddOnRow(addOn, blockId)).join('')}
        </div>
      ` : ''}

      ${optionalAddOns.length ? `
        <div class="aac-install-section-${blockId}" data-install-section="optional">
          <div class="aac-install-section-heading-${blockId}">
            <span class="aac-install-pill-${blockId} aac-install-pill--neutral-${blockId}">Optional</span>
          </div>
          ${optionalAddOns.map(addOn => this.renderAddOnRow(addOn, blockId)).join('')}
        </div>
      ` : ''}

      <div class="aac-install-total-${blockId}">
        <span class="aac-install-total-label-${blockId}">Bundle Total</span>
        <span class="aac-install-total-price-${blockId}" data-bundle-total>$0.00</span>
      </div>
    `;
  }

  static groupAdhesivesByProduct(recommendations) {
    const groupsByProduct = new Map();

    recommendations.forEach(recommendation => {
      const key = recommendation.productId || recommendation.product?.id || recommendation.title;
      const title = recommendation.product?.title || this.getProductTitleFromRecommendation(recommendation.title);

      if (!groupsByProduct.has(key)) {
        groupsByProduct.set(key, { title, items: [] });
      }

      groupsByProduct.get(key).items.push(recommendation);
    });

    return Array.from(groupsByProduct.values());
  }

  static getProductTitleFromRecommendation(title) {
    return String(title || '').split(' - ')[0];
  }

  static getVariantLabel(recommendation) {
    const variantTitle = recommendation.variant?.title;

    if (variantTitle && variantTitle !== 'Default Title') {
      return variantTitle;
    }

    const title = String(recommendation.title || '');
    const parts = title.split(' - ');
    return parts.length > 1 ? parts.slice(1).join(' - ') : title;
  }

  static renderAdhesiveGroup(group, blockId, labels, lowestVariantIds) {
    const sortedItems = [...group.items].sort(
      (a, b) => this.getAdhesiveOptionOrder(a) - this.getAdhesiveOptionOrder(b)
    );

    return `
      <div class="aac-install-adhesive-group-${blockId}">
        <h5 class="aac-install-adhesive-title-${blockId}">${this.escapeHtml(this.getAdhesiveGroupLabel(group.title))}</h5>
        <div class="aac-install-adhesive-options-${blockId}">
          ${sortedItems.map(recommendation => this.renderAdhesiveOption(recommendation, blockId, labels, lowestVariantIds)).join('')}
        </div>
      </div>
    `;
  }

  static getAdhesiveGroupLabel(title) {
    const normalizedTitle = String(title || '').toLowerCase();

    if (normalizedTitle.includes('epx2')) {
      return 'EPX2';
    }

    if (normalizedTitle.includes('epx3')) {
      return 'EPX3';
    }

    return title;
  }

  static getAdhesiveOptionOrder(recommendation) {
    const label = this.getVariantLabel(recommendation).toLowerCase().replace(/\s/g, '');

    if (label.includes('2.5lb')) return 10;
    if (label.includes('10lb')) return 20;
    if (label.includes('6x12oz')) return 30;
    return 100;
  }

  static getAdhesiveBenefit(recommendation, labels) {
    const label = this.getVariantLabel(recommendation).toLowerCase().replace(/\s/g, '');

    if (label.includes('2.5lb')) return labels.smallJobBenefit;
    if (label.includes('10lb')) return labels.largeJobBenefit;
    if (label.includes('6x12oz')) return labels.easyHandlingBenefit;
    return '';
  }

  static getPackageLabels(recommendation, labels) {
    const label = this.getVariantLabel(recommendation).toLowerCase().replace(/\s/g, '');

    if (label.includes('2.5lb')) return [labels.pailSingular, labels.pailPlural];
    if (label.includes('10lb')) return [labels.tubSingular, labels.tubPlural];
    if (label.includes('cartridge') || recommendation.requiresCaulkGun) {
      return [labels.cartridgeSingular, labels.cartridgePlural];
    }
    return [labels.packageSingular, labels.packagePlural];
  }

  static getRequiredQuantityText(recommendation, labels) {
    const [singular, plural] = this.getPackageLabels(recommendation, labels);
    const noun = recommendation.quantity === 1 ? singular : plural;
    return `${recommendation.quantity} ${noun} ${labels.required}`;
  }

  static renderAdhesiveOption(recommendation, blockId, labels, lowestVariantIds) {
    const { productId, variantId, unitPrice, totalPrice, quantity, ratio } = recommendation;
    const label = this.getVariantLabel(recommendation);
    const benefit = this.getAdhesiveBenefit(recommendation, labels);
    const quantityText = this.getRequiredQuantityText(recommendation, labels);
    const needsCaulkGun = recommendation.requiresCaulkGun === true;
    const isLowest = lowestVariantIds.get(String(productId)) === String(variantId);
    const inputId = `Adhesive-${blockId}-${variantId}`;

    return `
      <label
        class="aac-install-adhesive-row-${blockId}"
        data-selectable-option
        data-product-id="${productId}"
        data-variant-id="${variantId}"
        data-needs-caulk-gun="${needsCaulkGun}"
        data-adhesive-fill-ratio="${ratio}"
        data-unit-price="${unitPrice}"
        data-total-price="${totalPrice}"
        data-quantity="${quantity}"
      >
        <input
          id="${inputId}"
          type="radio"
          name="adhesive-${blockId}"
          value="${variantId}"
          data-adhesive-radio
        >
        <span class="aac-install-adhesive-details-${blockId}">
          <span class="aac-install-adhesive-heading-${blockId}">
            <span class="aac-install-adhesive-size-${blockId}">${this.escapeHtml(label)}</span>
            <span class="aac-install-lowest-badge-${blockId}" data-lowest-badge${isLowest ? '' : ' hidden'}>${this.escapeHtml(labels.lowestTotal)}</span>
          </span>
          <span
            class="aac-install-adhesive-meta-${blockId}"
            data-option-meta
            data-benefit="${this.escapeHtml(benefit)}"
          >${this.escapeHtml([benefit, quantityText].filter(Boolean).join(' · '))}</span>
        </span>
        <span class="aac-install-adhesive-total-${blockId}" data-total-price-display>+${AdhesiveCalculatorCore.formatMoney(totalPrice)}</span>
      </label>
    `;
  }

  static renderNoAdhesiveOption(blockId, labels) {
    return `
      <label class="aac-install-adhesive-row-${blockId} aac-install-adhesive-row--none-${blockId}">
        <input
          type="radio"
          name="adhesive-${blockId}"
          value="none"
          data-no-adhesive
        >
        <span class="aac-install-adhesive-details-${blockId}">
          <span class="aac-install-adhesive-size-${blockId}">${this.escapeHtml(labels.haveCompatibleAdhesive)}</span>
          <span class="aac-install-adhesive-meta-${blockId}">${this.escapeHtml(labels.noAdhesiveNote)}</span>
        </span>
        <span class="aac-install-adhesive-total-${blockId}">${AdhesiveCalculatorCore.formatMoney(0)}</span>
      </label>
    `;
  }

  static renderHelpLink(blockId, url, label) {
    if (!url) return '';

    return `<a
      class="aac-install-help-${blockId}"
      href="${this.escapeHtml(url)}"
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Learn more about ${this.escapeHtml(label)}"
    >?</a>`;
  }

  static renderAddOnRow(addOn, blockId) {
    const caulkGunGate = addOn.requiresCaulkGunAdhesive ? 'data-caulk-gun-addon' : '';

    if (addOn.outOfStock) {
      return `
        <div class="aac-install-row-${blockId} aac-install-row--out-of-stock-${blockId}" data-add-on-row ${caulkGunGate} role="note">
          <span>
            <span class="aac-install-item-title-${blockId}">${this.escapeHtml(addOn.title)}</span>
            ${addOn.note ? `<span class="aac-install-item-note-${blockId}">${this.escapeHtml(addOn.note)}</span>` : ''}
          </span>
          <a class="aac-install-product-link-${blockId}" href="${this.escapeHtml(addOn.helpUrl)}" target="_blank" rel="noopener noreferrer">View alternatives</a>
        </div>
      `;
    }

    const checked = addOn.selected ? 'checked' : '';
    const productLink = addOn.productUrl
      ? `<a class="aac-install-product-link-${blockId}" href="${this.escapeHtml(addOn.productUrl)}" data-product-link>View product</a>`
      : '';

    return `
      <label class="aac-install-row-${blockId}" data-add-on-row ${caulkGunGate}>
        <input
          type="checkbox"
          data-install-add-on
          data-product-id="${addOn.productId}"
          data-variant-id="${addOn.variantId}"
          data-price="${addOn.price}"
          ${checked}
        >
        <span>
          <span class="aac-install-item-title-${blockId}">${this.escapeHtml(addOn.title)}${this.renderHelpLink(blockId, addOn.helpUrl, addOn.title)}${productLink}</span>
          ${addOn.note ? `<span class="aac-install-item-note-${blockId}">${this.escapeHtml(addOn.note)}</span>` : ''}
        </span>
        <span class="aac-install-item-price-${blockId}">${AdhesiveCalculatorCore.formatMoney(addOn.price)}</span>
      </label>
    `;
  }

  static setupEventHandlers(config, currentProductId, recommendations) {
    const { containerId, settings } = config;
    const container = document.getElementById(containerId);

    if (!container) return;

    let selectedOption = null;
    let mainQuantity = 1;
    let hasAdhesiveChoice = settings.isKit === true;
    const productForm = this.getProductForm(container);
    let selectedVariantId = productForm?.querySelector('input[name="id"]')?.value || null;
    let pendingVariantSelection = Promise.resolve();
    let variantSelectionGeneration = 0;

    const selectableOptions = container.querySelectorAll('[data-selectable-option]');
    const noAdhesiveInput = container.querySelector('[data-no-adhesive]');
    const addOnInputs = container.querySelectorAll('[data-install-add-on]');
    const productLinks = container.querySelectorAll('[data-product-link]');

    const updateTotal = () => this.updateBundleTotal(container, selectedOption, mainQuantity, selectedVariantId);
    const refreshConditionalAddOns = () =>
      this.updateConditionalAddOns(container, selectedOption, settings.includedRequiresCaulkGun === true);

    const productScope = productForm?.closest('.shopify-section, dialog, product-card');
    productScope?.addEventListener('shopify:product:select', event => {
      const generation = ++variantSelectionGeneration;
      const requestedVariantId = event.detail?.variantId;

      if (requestedVariantId) {
        selectedVariantId = requestedVariantId;
        updateTotal();
      }

      pendingVariantSelection = event.promise
        ? event.promise
            .then(({ variant } = {}) => {
              if (generation !== variantSelectionGeneration) return;
              selectedVariantId = variant?.id || requestedVariantId || selectedVariantId;
              const currentProductForm = this.getProductForm(container);
              const currentAddToCartButton = this.getSubmitButton(currentProductForm);
              if (currentAddToCartButton) currentAddToCartButton.dataset.aacGuidedCart = 'true';
              updateTotal();
            })
            .catch(error => {
              if (error?.name !== 'AbortError') throw error;
            })
        : Promise.resolve();
    });

    selectableOptions.forEach(option => {
      const input = option.querySelector('[data-adhesive-radio]');

      input?.addEventListener('change', () => {
        if (!input.checked) return;

        selectedOption = option;
        hasAdhesiveChoice = true;
        this.clearProductFormError(productForm);
        refreshConditionalAddOns();
        updateTotal();
      });
    });

    noAdhesiveInput?.addEventListener('change', () => {
      if (!noAdhesiveInput.checked) return;

      selectedOption = null;
      hasAdhesiveChoice = true;
      this.clearProductFormError(productForm);
      refreshConditionalAddOns();
      updateTotal();
    });

    addOnInputs.forEach(input => {
      input.addEventListener('change', updateTotal);
    });

    productLinks.forEach(link => {
      link.addEventListener('click', event => {
        event.stopPropagation();
      });
    });

    this.observeQuantityChanges(
      container,
      selectableOptions,
      () => mainQuantity,
      qty => {
        mainQuantity = qty;
      },
      currentProductId,
      recommendations,
      settings.labels,
      updateTotal
    );
    refreshConditionalAddOns();
    this.setupGuidedCartSubmit(
      container,
      currentProductId,
      () => selectedOption,
      () => hasAdhesiveChoice,
      () => mainQuantity,
      () => this.getSelectedAddOns(container),
      () => selectedVariantId,
      () => pendingVariantSelection,
      settings.labels
    );
    updateTotal();
  }

  static updateBundleTotal(container, selectedOption, mainQuantity, selectedVariantIdOverride = null) {
    const productForm = this.getProductForm(container);
    const selectedVariantId =
      selectedVariantIdOverride || productForm?.querySelector('input[name="id"]')?.value;
    let total = this.getMainProductPrice(selectedVariantId, container) * mainQuantity;

    if (selectedOption) {
      const unitPrice = parseInt(selectedOption.getAttribute('data-unit-price'), 10) || 0;
      const quantity = parseInt(selectedOption.getAttribute('data-quantity'), 10) || 1;
      total += unitPrice * quantity;
    }

    this.getSelectedAddOns(container).forEach(addOn => {
      total += addOn.price;
    });

    const totalDisplay = container.querySelector('[data-bundle-total]');
    if (totalDisplay) {
      totalDisplay.textContent = AdhesiveCalculatorCore.formatMoney(total);
    }
  }

  static getMainProductPrice(selectedVariantId, container) {
    const normalizedVariantId = String(selectedVariantId || '').split('/').pop();
    const configuredVariantPrices = container?.dataset.variantPrices;

    if (configuredVariantPrices) {
      try {
        const variantPrices = JSON.parse(configuredVariantPrices);
        const selectedPrice =
          variantPrices[String(selectedVariantId)] ?? variantPrices[normalizedVariantId];
        const parsedSelectedPrice = parseInt(selectedPrice, 10);
        if (parsedSelectedPrice) return parsedSelectedPrice;
      } catch (error) {
        console.warn('Unable to read configured variant prices for bundle total', error);
      }
    }

    const configuredPrice = parseInt(container?.dataset.mainProductPrice, 10);
    if (configuredPrice) return configuredPrice;

    const productJson = document.querySelector('script[type="application/json"][data-product-json]');

    if (productJson) {
      try {
        const product = JSON.parse(productJson.textContent);
        const variant = product?.variants?.find(item => String(item.id) === String(selectedVariantId));
        if (variant?.price) return variant.price;
      } catch (error) {
        console.warn('Unable to read product JSON for bundle total', error);
      }
    }

    const variantData = window.variantData || window.product?.variants || [];
    const variant = Array.isArray(variantData)
      ? variantData.find(item => String(item.id) === String(selectedVariantId))
      : null;

    return parseInt(variant?.price, 10) || 0;
  }

  static getSelectedAddOns(container) {
    return Array.from(container.querySelectorAll('[data-install-add-on]:checked')).map(input => ({
      productId: input.getAttribute('data-product-id'),
      variantId: input.getAttribute('data-variant-id'),
      price: parseInt(input.getAttribute('data-price'), 10) || 0,
      quantity: 1
    }));
  }

  static updateConditionalAddOns(container, selectedOption, includedRequiresCaulkGun = false) {
    const adhesiveNeedsCaulkGun =
      includedRequiresCaulkGun || selectedOption?.getAttribute('data-needs-caulk-gun') === 'true';

    container.querySelectorAll('[data-caulk-gun-addon]').forEach(row => {
      row.hidden = !adhesiveNeedsCaulkGun;

      if (!adhesiveNeedsCaulkGun) {
        const checkbox = row.querySelector('[data-install-add-on]');
        if (checkbox && checkbox.checked) checkbox.checked = false;
      }
    });

    container.querySelectorAll('[data-install-section]').forEach(section => {
      const rows = section.querySelectorAll('[data-add-on-row]');
      if (rows.length === 0) return;

      const hasVisibleRow = Array.from(rows).some(row => !row.hidden);
      section.hidden = !hasVisibleRow;
    });
  }

  static moveQuantitySelectorIntoInstallFlow(container, blockId) {
    const quantitySlot = container.querySelector('[data-anchor-quantity-slot]');
    const productForm = this.getProductForm(container);
    const quantityWrapper = productForm?.querySelector('[ref="quantitySelectorWrapper"]');
    const quantityRules = productForm?.querySelector('[ref="quantityRules"]');

    if (!quantitySlot || !quantityWrapper) return;

    quantitySlot.append(quantityWrapper);

    if (quantityRules) {
      quantitySlot.append(quantityRules);
    }
  }

  static placeOwnedQuantitySelector(container, ownedQuantity) {
    const quantitySlot = container.querySelector('[data-anchor-quantity-slot]');
    if (!quantitySlot || !ownedQuantity) return;

    while (ownedQuantity.firstElementChild) {
      quantitySlot.append(ownedQuantity.firstElementChild);
    }
  }

  static observeQuantityChanges(
    container,
    selectableOptions,
    getMainQuantity,
    setMainQuantity,
    currentProductId,
    recommendations,
    labels,
    onQuantitiesUpdated = () => {}
  ) {
    const quantitySlot = container.querySelector('[data-anchor-quantity-slot]');
    const mainQuantityInput = quantitySlot?.querySelector(
      '[ref="quantitySelectorWrapper"] input[name="quantity"]'
    );

    const updateAllQuantities = newMainQuantity => {
      setMainQuantity(newMainQuantity);

      const updatedRecommendations = AdhesiveCalculatorCore.updateQuantities(
        recommendations,
        newMainQuantity,
        currentProductId
      );

      this.updateAdhesiveOptionDisplays(selectableOptions, updatedRecommendations, labels);
      onQuantitiesUpdated();
    };

    if (mainQuantityInput) {
      updateAllQuantities(parseInt(mainQuantityInput.value, 10) || 1);

      quantitySlot.addEventListener('quantity-selector:update', event => {
        updateAllQuantities(parseInt(event.detail?.quantity, 10) || 1);
      });
      mainQuantityInput.addEventListener('input', event => {
        updateAllQuantities(parseInt(event.target.value, 10) || 1);
      });
    }
  }

  static updateAdhesiveOptionDisplays(selectableOptions, recommendations, labels) {
    const lowestVariantIds = AdhesiveCalculatorCore.getLowestTotalVariantIdsByProduct(recommendations);

    selectableOptions.forEach(option => {
      const variantId = option.getAttribute('data-variant-id');
      const recommendation = recommendations.find(item => String(item.variantId) === String(variantId));

      if (!recommendation) return;

      option.setAttribute('data-quantity', String(recommendation.quantity));
      option.setAttribute('data-total-price', String(recommendation.totalPrice));

      const benefit = option.querySelector('[data-option-meta]')?.getAttribute('data-benefit') || '';
      const quantityText = this.getRequiredQuantityText(recommendation, labels);
      const meta = option.querySelector('[data-option-meta]');
      const total = option.querySelector('[data-total-price-display]');
      const badge = option.querySelector('[data-lowest-badge]');

      if (meta) meta.textContent = [benefit, quantityText].filter(Boolean).join(' · ');
      if (total) total.textContent = `+${AdhesiveCalculatorCore.formatMoney(recommendation.totalPrice)}`;
      if (badge) {
        badge.hidden = lowestVariantIds.get(String(recommendation.productId)) !== String(recommendation.variantId);
      }
    });
  }

  static setupGuidedCartSubmit(
    container,
    currentProductId,
    getSelectedOption,
    getHasAdhesiveChoice,
    getMainQuantity,
    getSelectedAddOns,
    getSelectedVariantId,
    waitForVariantSelection,
    labels
  ) {
    const productForm = this.getProductForm(container);
    const form = productForm?.querySelector('form[action*="/cart/add"]');
    const addToCartButton = this.getSubmitButton(productForm);
    const ownsSubmission = addToCartButton?.dataset.aacSubmit === 'aac-install-flow';

    if (!form) return;

    let isSubmitting = false;
    if (addToCartButton) addToCartButton.dataset.aacGuidedCart = 'true';

    const submitGuidedCart = async function(event) {
      event.preventDefault();
      event.stopImmediatePropagation();

      if (isSubmitting) return;

      const selectedOption = getSelectedOption();
      const selectedAddOns = getSelectedAddOns();
      const mainQuantity = getMainQuantity();

      if (!getHasAdhesiveChoice()) {
        AdhesiveBlockRenderer.showProductFormError(productForm, labels.chooseAdhesiveError);
        return;
      }

      isSubmitting = true;
      addToCartButton?.setAttribute('aria-busy', 'true');

      try {
        await waitForVariantSelection();

        const variantId = getSelectedVariantId() || form.querySelector('input[name="id"]')?.value;
        if (!variantId) {
          AdhesiveBlockRenderer.showProductFormError(
            productForm,
            'Please select an available option before adding to cart.'
          );
          return;
        }

        const adhesive = selectedOption
          ? {
            variantId: selectedOption.getAttribute('data-variant-id'),
            productId: selectedOption.getAttribute('data-product-id'),
            quantity: parseInt(selectedOption.getAttribute('data-quantity'), 10) || 1
          }
          : null;
        const lines = window.AacCommerceDomain.guidedPurchaseLines({
          anchorVariantId: variantId,
          anchorPackageQuantity: mainQuantity,
          adhesive,
          accessories: selectedAddOns,
        });

        if (ownsSubmission) {
          AdhesiveBlockRenderer.animateGuidedAdd(addToCartButton, event);
        }

        await AdhesiveBlockRenderer.addCartLines(lines, {
          currentProductId,
          sourceId: container.id
        });

        AdhesiveBlockRenderer.clearProductFormError(productForm);
        AdhesiveBlockRenderer.announceCartSuccess(productForm, addToCartButton);
      } catch (error) {
        console.error('Error adding guided install items:', error);
        AdhesiveBlockRenderer.showProductFormError(
          productForm,
          error?.message || 'Unable to add the install bundle. Please try again.'
        );
      } finally {
        isSubmitting = false;
        addToCartButton?.removeAttribute('aria-busy');
      }
    };

    if (addToCartButton) {
      addToCartButton.addEventListener('click', submitGuidedCart, !ownsSubmission);
    }

    form.addEventListener('keydown', event => {
      if (event.key !== 'Enter') return;
      if (event.target instanceof HTMLTextAreaElement) return;

      submitGuidedCart(event);
    }, true);
  }

  static async addCartItems(items, eventDetail = {}) {
    const lines = window.AacCommerceDomain.cartLines(items);
    return this.addCartLines(lines, eventDetail);
  }

  static async addCartLines(lines, eventDetail = {}) {
    if (lines.length === 0) return null;

    const updateCart = window.Shopify?.actions?.updateCart;
    if (typeof updateCart !== 'function') {
      throw new Error('Cart actions are unavailable. Please refresh the page and try again.');
    }

    const result = await updateCart(
      {
        lines
      },
      {
        event: {
          context: 'product',
          detail: {
            source: 'aac-install-flow',
            sourceId: eventDetail.sourceId,
            itemCount: lines.reduce((sum, line) => sum + line.quantity, 0),
            productId: String(eventDetail.currentProductId || '')
          }
        }
      }
    );

    if (result?.userErrors?.length) {
      throw new Error(result.userErrors.map(error => error.message).filter(Boolean).join(' '));
    }

    return result;
  }

  static announceCartSuccess(productForm, addToCartButton) {
    const liveRegion = productForm?.querySelector('[ref="liveRegion"]');
    if (!liveRegion) return;

    const addedText =
      addToCartButton?.querySelector('.add-to-cart-text--added')?.textContent?.trim() ||
      'Items added to cart.';

    liveRegion.textContent = addedText;
    setTimeout(() => {
      if (liveRegion.textContent === addedText) liveRegion.textContent = '';
    }, 5000);
  }

  static getProductForm(container) {
    const formId = container?.dataset?.productFormId;
    const explicitForm = formId ? document.getElementById(formId) : null;
    if (explicitForm) return explicitForm.closest('product-form-component');

    const productScope = container?.closest?.('.shopify-section, dialog, product-card');
    return productScope?.querySelector('product-form-component') || null;
  }

  static getSubmitButton(productForm) {
    return productForm?.querySelector('[data-aac-submit="aac-install-flow"]') ||
      productForm?.querySelector('[ref="addToCartButton"]') || null;
  }

  static animateGuidedAdd(addToCartButton, event) {
    const addToCartComponent = addToCartButton?.closest?.('add-to-cart-component');
    if (typeof addToCartComponent?.handleClick === 'function') {
      addToCartComponent.handleClick(event);
    }
  }

  static showProductFormError(productForm, message) {
    const errorElement = productForm?.querySelector('[ref="addToCartTextError"]');

    if (!errorElement) {
      console.error(message);
      return;
    }

    errorElement.classList.remove('hidden');
    errorElement.setAttribute('aria-live', 'assertive');

    const textNode = Array.from(errorElement.childNodes).find(node => node.nodeType === Node.TEXT_NODE);
    if (textNode) {
      textNode.textContent = message;
    } else {
      errorElement.appendChild(document.createTextNode(message));
    }
  }

  static clearProductFormError(productForm) {
    const errorElement = productForm?.querySelector('[ref="addToCartTextError"]');
    if (!errorElement) return;

    errorElement.classList.add('hidden');
    errorElement.removeAttribute('aria-live');
  }

  static escapeHtml(value) {
    return String(value ?? '').replace(/[&<>"']/g, character => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;'
    })[character]);
  }
}

window.AdhesiveBlockRenderer = AdhesiveBlockRenderer;
